import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { BasePage } from './bases/BasePage';
import { ManageForm } from '@components/forms/ManageForm';
import { Grid, Button, Link, Typography, List, ListItem } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import templateUrl from '../assets/template.csv?url';
import { UploadButton } from '@components/buttons/UploadButton';
import { useState } from 'react';
import { useStyles } from './styles/BatchProcessing.styles';
import { readFileAsText } from '@utils/upload';
import { nanoid } from '@reduxjs/toolkit';
import { uploadClaims } from '@api/cutstate';
import { useSelector } from 'react-redux';
import ConfettiExplosion from 'react-confetti-explosion';

import type { RootState } from '@features/redux/store';
import { useAlert } from '@hooks/useAlert';
import { useSetLoading } from '@hooks/useSetLoading';
import { Validate } from '@utils/validate';
import { PaperModal } from '@components/dialogs/PaperModal';
import { getCells } from '@utils/csv';

export const BatchProcessing = () => {
    const { IdToken } = useSelector((state: RootState) => state.auth);
    const classes = useStyles();

    const [file, setFile] = useState<File | null>(null);
    const [invalidIds, setInvalidIds] = useState<string[]>([]);

    const { setAlert } = useAlert();

    const [exploding, setExploding] = useState(false);
    const { loading, setLoading } = useSetLoading();

    const handleSubmit = async () => {
        if (!file) return;
        setLoading(true);

        try {
            const body = {
                key: `${Date.now()}-${file.name || `${nanoid(10)}.csv`}`,
                csv: await readFileAsText(file),
            };

            await uploadClaims({
                token: IdToken!,
                body,
            });

            setFile(null);
            setAlert(`Successfully uploaded "${file.name}"`, 'success');
            setExploding(true);
        } catch (error) {
            setAlert('Failed to upload file!', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <BasePage>
            <BreadcrumbSubBar />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <ManageForm title='Batch Processing' size={12}>
                    <Grid item md={6} sm={12} lg={6} display='flex' flexDirection='column' gap='20px'>
                        <Typography fontWeight='bold'>1. Download the template .csv file:</Typography>
                        {/* STATIC DOWNLOAD LINK */}
                        <Link download href={templateUrl}>
                            <Button startIcon={<AssignmentIcon />} variant='contained' color='orange' size='large'>
                                Download Template CSV
                            </Button>
                        </Link>
                        <Typography fontWeight='bold'>
                            2. Use the template to create a batch processing file.
                        </Typography>
                        <Typography fontWeight='bold'>3. Upload your batch processing file:</Typography>
                        <UploadButton
                            className={classes.grower}
                            buttonSize={'large'}
                            text={file ? file.name : undefined}
                            multiple={false}
                            accept='text/csv'
                            onUpload={async ([file]) => {
                                const csv = await readFileAsText(file);

                                const invalid = getCells(csv).filter((id) => !Validate.Cut.id(id));

                                if (invalid.length) setInvalidIds(invalid);
                                else setFile(file);
                            }}
                        />
                        <Typography fontWeight='bold'>4. Submit it.</Typography>
                        <Button
                            variant='contained'
                            size='large'
                            sx={{ width: '135px' }}
                            disabled={!file || loading}
                            onClick={handleSubmit}>
                            Submit
                        </Button>
                        <Typography fontWeight='bold'>5. Party time.</Typography>
                    </Grid>
                </ManageForm>

                {exploding && (
                    <ConfettiExplosion
                        className={classes.confetti}
                        zIndex={999_999}
                        duration={3_500}
                        onComplete={() => {
                            setExploding(false);
                        }}
                        particleCount={200}
                        particleSize={10}
                    />
                )}
            </div>

            <PaperModal
                title='Upload failed!'
                open={Boolean(invalidIds.length)}
                buttons={[{ text: 'Ok', onClick: () => setInvalidIds([]) }]}>
                <Typography fontWeight='bold' color='red'>
                    The following Cut IDs are invalid:
                </Typography>

                <Typography>
                    <b>Note:</b> Cut ID prefixes can only contain numbers and the letters A-F.
                </Typography>

                <List dense>
                    {invalidIds.map((id) => (
                        <ListItem key={`invalid-id-${id}`}>
                            <Typography color='red'>{id}</Typography>
                        </ListItem>
                    ))}
                </List>
            </PaperModal>
        </BasePage>
    );
};
